import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Row,
	Col,
	Typography,
	message,
	notification,
	Space,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContext from "../../store/app.context";
import { requestCreateServiceGroup } from "../../services/admin.service";
import { PiArrowLeftDuotone } from "react-icons/pi";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Constants based on schema
const STATUS_OPTIONS = [
	{ label: "Active", value: "ACTIVE" },
	{ label: "Inactive", value: "INACTIVE" },
];

function AddServiceGroupPage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		document.title = "Cloudiva Console | Add Service Group";
	}, []);

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				setLoading(true);

				// Process the form data to match the schema
				const serviceGroupData = {
					code: values.code,
					name: values.name,
					description: values.description || "",
					status: values.status || "ACTIVE",
					metadata: values.metadata ? JSON.parse(values.metadata) : {},
				};

				requestCreateServiceGroup(serviceGroupData)
					.then(() => {
						message.success("Service group created successfully!");
						navigate("/service-groups");
					})
					.catch((error) => {
						console.error("Error creating service group:", error);
						if (error.response?.data?.message) {
							notification.error({
								message: "Error",
								description: error.response.data.message,
							});
						} else {
							notification.error({
								message: "Error",
								description: "Failed to create service group. Please try again.",
							});
						}
					})
					.finally(() => {
						setLoading(false);
					});
			})
			.catch((error) => {
				console.error("Validation failed:", error);
			});
	};

	const validateJSON = (_, value) => {
		if (!value) return Promise.resolve();
		try {
			JSON.parse(value);
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(new Error("Please enter valid JSON"));
		}
	};

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button
						icon={<PiArrowLeftDuotone />}
						onClick={() => navigate("/service-groups")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Add Service Group
					</Title>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title="Create New Service Group"
					extra={
						<div>
							<Button onClick={() => navigate("/service-groups")} style={{ marginRight: 8 }}>
								Cancel
							</Button>
							<Button type="primary" loading={loading} onClick={handleSubmit}>
								Create Service Group
							</Button>
						</div>
					}>
					<Form form={form} layout="vertical">
						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									label="Service Group Code"
									name="code"
									rules={[
										{ required: true, message: "Please enter service group code" },
										{
											pattern: /^[A-Z0-9_-]+$/,
											message:
												"Code must contain only uppercase letters, numbers, underscores, and hyphens",
										},
									]}>
									<Input placeholder="Enter service group code (e.g., COMPUTE_SERVICES)" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Service Group Name"
									name="name"
									rules={[{ required: true, message: "Please enter service group name" }]}>
									<Input placeholder="Enter service group name" />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item label="Description" name="description">
									<TextArea rows={4} placeholder="Enter service group description" />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									label="Metadata (JSON)"
									name="metadata"
									rules={[{ validator: validateJSON }]}>
									<TextArea
										rows={6}
										placeholder='Enter metadata as JSON (optional) e.g., {"priority": 1, "category": "infrastructure"}'
									/>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</Card>
			</div>
		</div>
	);
}

export default AddServiceGroupPage;
